import { useLazyQuery } from "@apollo/client/react";
import OttoIcon from "@assets/imgs/Otto-Icon.svg";
import QuillIcon from "@assets/imgs/Quill-Icon.svg";
import SageIcon from "@assets/imgs/Sage-Icon.svg";
import SummiIcon from "@assets/imgs/Summi-Icon.svg";
import GET_COMPANION_SESSION from "@graphql/queries/GetCompanionSession";
import {
  useLocalStorage,
  NUMBER_SERIALIZATION,
} from "@scribenote/utils";
import mixpanel from "mixpanel-browser";
import { useState, createContext, useContext } from "react";

import { useAuth } from "./use-auth";

// Sidebar sizing constants
export const SIDEBAR_COLLAPSED_WIDTH = 64;
export const SIDEBAR_MIN_WIDTH = 210;
export const SIDEBAR_SNAP_DISTANCE = 128;

export const getDefaultWidth = () =>
  Math.min(
    window.innerWidth,
    Math.max(
      SIDEBAR_MIN_WIDTH + 1,
      Math.min(Math.round(window.innerWidth * 0.4), 900),
    ),
  );

const CompanionContext = createContext();

export const CompanionIntroMessage = `Hi there! We're your veterinary AI assistants:

🐘 Summi — I can help summarize patient history from uploaded PDF records!
🦔 Quill — I can help edit or improve your notes!
🐢 Sage — I can help answer any veterinary question you may have by researching on the web!

Just type your question and we'll figure out who's best to help! You can also tag us directly with @summi, @quill, or @sage.

We're still learning, and AI can make mistakes. Remember to double-check our work!`;

export const AvailableCompanions = {
  OTTO: {
    name: "otto",
    displayName: "Otto",
    description: "Your Support Companion",
    emoji: "🦮",
    headImage: OttoIcon,
  },
  SUMMI: {
    name: "summi",
    displayName: "Summi",
    description: "Your History Companion",
    emoji: "🐘",
    headImage: SummiIcon,
    welcomeMessage:
      "Hi there, I'm Summi 🐘, here to help you turn patient records into a concise patient history. Upload a PDF, then you can ask me questions about it by typing @summi!",
  },
  QUILL: {
    name: "quill",
    displayName: "Quill",
    description: "Your Writing Companion",
    emoji: "🦔",
    headImage: QuillIcon,
    welcomeMessage:
      "I'm Quill 🦔, try asking me to make a change when you're editing your notes by typing @quill!",
  },
  SAGE: {
    name: "sage",
    displayName: "Sage",
    description: "Your Research Companion",
    emoji: "🐢",
    headImage: SageIcon,
    welcomeMessage:
      "I'm Sage 🐢, here to answer all your trickiest questions - just tag me in by typing @sage! It might take me a few minutes to venture off and seek the answers you’re looking for! I’m just an old tortoise after all…",
  },
};

export const AvailableCompanionTabs = {
  history: "history",
  chat: "chat",
  notifications: "notifications",
  settings: "settings",
  help: "help",
};

export const AvailableSettingsSections = {
  billing: "billing",
  permissions: "permissions",
};

export function CompanionV1Provider({ children }) {
  const [activeTab, _setActiveTab] = useState(
    AvailableCompanionTabs.chat,
  );
  const [companionSession, setCompanionSession] = useState(null);
  const [settingsSection, setSettingsSection] = useState(
    AvailableSettingsSections.billing,
  );
  const { isUserAuthenticated } = useAuth();

  const [sidebarWidth, setSidebarWidth] = useLocalStorage(
    "companionSidebarWidth",
    SIDEBAR_COLLAPSED_WIDTH,
    NUMBER_SERIALIZATION,
  );
  const isSidebarCollapsed = sidebarWidth <= SIDEBAR_MIN_WIDTH;
  const expandSidebar = () => {
    if (isSidebarCollapsed) setSidebarWidth(getDefaultWidth());
  };

  const setActiveTab = (newTab) => {
    mixpanel.track("Switched Companion Sidebar Tab", {
      companionSessionUuid: companionSession?.companionSessionUuid,
      newTab: newTab,
    });
    _setActiveTab(newTab);
  };

  if (!isUserAuthenticated && companionSession !== null) {
    setCompanionSession(null);
  }

  const [getCompanionSession] = useLazyQuery(GET_COMPANION_SESSION, {
    fetchPolicy: "no-cache",
  });

  let mostRecentCompanionUsed = null;
  const companions = Object.keys(AvailableCompanions);
  const messages = companionSession?.messages;
  if (messages?.length) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (
        message?.companionType &&
        companions.includes(message.companionType)
      ) {
        mostRecentCompanionUsed = message.companionType;
        break;
      }

      if (message?.textContent) {
        const foundCompanion = companions.find((companion) =>
          message.textContent
            .toLowerCase()
            .includes(`@${companion.toLowerCase()}`),
        );
        if (foundCompanion) {
          mostRecentCompanionUsed = foundCompanion;
          break;
        }
      }
    }
  }

  return (
    <CompanionContext.Provider
      value={{
        companionSession,
        setCompanionSession,
        activeTab,
        setActiveTab,
        settingsSection,
        setSettingsSection,
        getCompanionSession,
        mostRecentCompanionUsed,
        sidebarWidth,
        setSidebarWidth,
        expandSidebar,
      }}
    >
      {children}
    </CompanionContext.Provider>
  );
}

export function useCompanion() {
  return useContext(CompanionContext);
}
